import request from '../utils/request';
import { getSessionIdentity } from '../hooks/useSessionIdentity';
import { getUser } from '../utils/func';
import { invalidateFeedSnapshots } from '../utils/searchFeedReturn';

const rows = new Map(), pending = new Map(), listeners = new Set(), queued = new Set();
let owner, scheduled = false, revision = 0;
const prefix = () => (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const validId = id => Number.isSafeInteger(Number(id)) && Number(id) > 0 && Number(id) <= 2147483647;
const validState = row => row && validId(row.user_id) && typeof row.blocked_by_viewer === 'boolean'
  && typeof row.interaction_blocked === 'boolean' && (!row.blocked_by_viewer || row.interaction_blocked);
const notify = () => { revision += 1; listeners.forEach(listener => listener()); };
const publishChange = (row, identity) => {
  invalidateFeedSnapshots();
  window.dispatchEvent(new CustomEvent('wiw:blocking-changed', { detail: { userId: Number(row.user_id), blocked: row.blocked_by_viewer, identity } }));
};
const session = () => {
  const next = getSessionIdentity();
  if (owner !== next) { owner = next; rows.clear(); pending.clear(); queued.clear(); }
  return next;
};
export const subscribeBlocking = callback => { listeners.add(callback); return () => listeners.delete(callback); };
export const blockingRevision = () => revision;
export const readBlockState = id => { session(); return rows.get(Number(id)) || null; };

async function flush() {
  scheduled = false;
  const identity = session(), ids = [...queued].slice(0, 100);
  ids.forEach(id => queued.delete(id));
  const versions = ids.map(id => rows.get(id));
  if (!ids.length || !getUser()?.id) return;
  try {
    const response = await request({ url: prefix() + '/user/blocks/state', method: 'POST', data: { user_ids: ids }, timeout: 10000 });
    if (identity !== getSessionIdentity()) return;
    if (!response.success || !Array.isArray(response.items) || response.items.length !== ids.length
      || !response.items.every(validState) || new Set(response.items.map(row => Number(row.user_id))).size !== ids.length
      || response.items.some(row => !ids.includes(Number(row.user_id)))) throw new Error('BLOCK_STATE_UNAVAILABLE');
    for (const row of response.items) {
      const id = Number(row.user_id), index = ids.indexOf(id);
      if (rows.get(id) === versions[index] && !pending.has(id)) {
        const before = rows.get(id);
        rows.set(id, { ...row, loaded: true, expires: Date.now() + 30000 });
        if (before?.blocked_by_viewer !== row.blocked_by_viewer && (before?.loaded || row.blocked_by_viewer)) publishChange(row, identity);
      }
    }
  } catch {
    if (identity !== getSessionIdentity()) return;
    ids.forEach((id, index) => { if (rows.get(id) === versions[index] && !pending.has(id)) rows.set(id, { ...rows.get(id), error: true, loading: false }); });
  }
  notify();
  if (queued.size) schedule();
}
const schedule = () => { if (!scheduled) { scheduled = true; queueMicrotask(flush); } };
export function loadBlockState(id, force = false) {
  session(); id = Number(id);
  if (!validId(id) || !getUser()?.id || id === getUser().id || pending.has(id)) return;
  const current = rows.get(id);
  if (!force && (current?.loading || current?.expires > Date.now())) return;
  rows.set(id, { ...current, loading: true, error: false }); queued.add(id); notify(); schedule();
}

export function changeBlock(id, blocked) {
  const identity = session(); id = Number(id);
  if (!validId(id) || !getUser()?.id || id === getUser().id) return Promise.reject(new Error('BLOCK_INVALID_USER'));
  if (pending.has(id)) return pending.get(id);
  rows.set(id, { ...rows.get(id), busy: true, error: false });
  const operation = Promise.resolve().then(async () => {
    let accepted = false;
    try {
      if (identity !== getSessionIdentity()) throw new Error('BLOCK_SESSION_CHANGED');
      const result = await request({ url: `${prefix()}/user/${id}/block`, method: blocked ? 'PUT' : 'DELETE', timeout: 10000 });
      if (identity !== getSessionIdentity()) throw new Error('BLOCK_SESSION_CHANGED');
      if (!result.success || !validState(result) || Number(result.user_id) !== id || result.blocked_by_viewer !== blocked) throw new Error('BLOCK_SAVE_FAILED');
      rows.set(id, { ...result, loaded: true, expires: Date.now() + 30000 });
      accepted = true;
      publishChange(result, identity);
      return result;
    } finally {
      if (identity === getSessionIdentity() && pending.get(id) === operation) {
        pending.delete(id);
        rows.set(id, { ...rows.get(id), busy: false, loading: false, ...(!accepted ? { expires: 0 } : {}) });
        notify();
        // A response can be lost after the server committed. Reconcile once,
        // never repeat the write automatically or leave an orphaned read lock.
        if (!accepted) loadBlockState(id, true);
      }
    }
  });
  pending.set(id, operation); notify();
  return operation;
}

export async function readBlockedUsers({ query = '', cursor, signal, limit = 20 } = {}) {
  const identity = session();
  const response = await request({ url: prefix() + '/user/blocks', method: 'GET', params: { q: query, limit, ...(cursor ? { cursor } : {}) }, signal, timeout: 10000 });
  if (identity !== getSessionIdentity() || signal?.aborted) throw new Error('BLOCK_SESSION_CHANGED');
  if (!response.success || !Array.isArray(response.items) || !response.items.every(row => validId(row.id) && typeof row.user_name === 'string')
    || !Number.isSafeInteger(response.blocked_count) || response.blocked_count < 0
    || !(response.next_cursor === null || typeof response.next_cursor === 'string')) throw new Error('BLOCK_LIST_UNAVAILABLE');
  return response;
}
